import React, { useState, useRef, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Card } from "reactstrap";
import {
  Canvas,
  Image as FabricImage,
  Textbox,
  Rect,
  Ellipse,
  Circle,
  Line,
  Triangle,
} from "fabric";
import { replaceInlinePlaceholdersAndStyles } from "components/CommonComponents/CertificatePreview";

const certificateValuesSmall = {
  trainee_name: "Amit",
  instructor_name: "Raj",
  course_name: "AWS",
  issue_date: "12 Nov 2024",
  certificate_no: "RCR123",
  completion_date: "12 Dec 2024",
  certificate_valid_till: "12 Dec 2027",
};

const certificateValuesMedium = {
  trainee_name: "Amit Narayan Parmar",
  instructor_name: "Raj Chauhan",
  course_name: "AWS Certified Solutions",
  issue_date: "12 November 2024",
  certificate_no: "raycham123",
  completion_date: "12 December 2024",
  certificate_valid_till: "12 December 2027",
};

const certificateValuesLarge = {
  trainee_name: "Venkateshwar Srinivasa Ramachandra Krishnamurthy",
  instructor_name: "Rajeshwar Chauhan Singh Rajput",
  course_name: "AWS Certified Solutions Architect - Associate Level Complete Masterclass 2024",
  issue_date: "12th November 2024",
  certificate_no: "raycham-cert-123-abc-999",
  completion_date: "12th December 2024",
  certificate_valid_till: "12th December 2027",
};

const FabricCertificateRender = ({ template, values, scale = 1, onCanvasReady }) => {
  const canvasContainerRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!template) return;

    const { width, height } = template.canvasSize;

    const canvas = new Canvas(canvasRef.current, {
      width,
      height,
      selection: false,
    });

    if (onCanvasReady) {
      onCanvasReady(canvas);
    }

    const render = async () => {
      let isBlank = false;
      if (template.backgroundType === "image" && template.backgroundImageSrc) {
        const bgUrl = template.backgroundImageSrc;
        isBlank = typeof bgUrl === 'string' && bgUrl.includes("blank.png");
        const bg = await FabricImage.fromURL(bgUrl);
        bg.set({
          left: canvas.width / 2,
          top: canvas.height / 2,
          originX: "center",
          originY: "center",
          selectable: false,
          evented: false,
        });
        bg.scaleX = canvas.width / bg.width;
        bg.scaleY = canvas.height / bg.height;
        bg.setCoords();
        canvas.backgroundImage = bg;
      } else {
        isBlank = true;
        const bgUrl = require("assets/img/certificates_templates/blank.png");
        const bg = await FabricImage.fromURL(bgUrl);
        bg.set({
          left: canvas.width / 2,
          top: canvas.height / 2,
          originX: "center",
          originY: "center",
          selectable: false,
          evented: false,
        });
        bg.scaleX = canvas.width / bg.width;
        bg.scaleY = canvas.height / bg.height;
        bg.setCoords();
        canvas.backgroundImage = bg;
      }

      if (canvasContainerRef.current) {
        const upperCanvasEls = canvasContainerRef.current.querySelectorAll(".upper-canvas");
        upperCanvasEls.forEach(el => {
          if (isBlank) el.classList.add("blank-canvas-border");
          else el.classList.remove("blank-canvas-border");
        });
      }

      for (let item of template.layout) {
        if (item.type === "shape" && item.text) {
          try {
            const shapeData = JSON.parse(item.text);
            item = { ...item, ...shapeData };
          } catch (e) {}
        }

        const left = (item.x / 100) * canvas.width;
        const top = (item.y / 100) * canvas.height;

        if (item.type === "text") {
          let extraStyles = {};
          try {
            let srcStr = item.Src ?? item.src;
            if (srcStr) {
              const jsonStart = srcStr.indexOf('{');
              if (jsonStart !== -1) {
                srcStr = srcStr.substring(jsonStart);
                extraStyles = JSON.parse(srcStr);
              }
            }
          } catch(e){}

          const originalStyles = item.styles || extraStyles.styles || {};
          const { value, styles: remappedStyles } = replaceInlinePlaceholdersAndStyles(item.text, originalStyles, values);

          canvas.add(
            new Textbox(value, {
              left,
              top,
              width: item.width || undefined,
              fontSize: item.fontSize,
              fontFamily: item.fontFamily,
              fontWeight: extraStyles.fontWeight || item.fontWeight || "normal",
              fontStyle: extraStyles.fontStyle || item.fontStyle || "normal",
              underline: extraStyles.underline !== undefined ? extraStyles.underline : (item.underline || false),
              fill: item.color || item.Color,
              angle: item.angle || item.Angle || 0,
              textAlign: item.textAlign || item.TextAlign || "left",
              originX: item.originX || item.OriginX || "center",
              originY: item.originY || item.OriginY || "center",
              scaleX: item.scaleX || item.ScaleX || 1,
              scaleY: item.scaleY || item.ScaleY || 1,
              lineHeight: item.lineHeight || 1,
              ...(Object.keys(remappedStyles).length > 0 && { styles: remappedStyles }),
              selectable: false,
              evented: false,
            }),
          );
        }

        if (item.type === "image") {
          const img = await FabricImage.fromURL(item.src);
          img.set({
            left, top, originX: item.originX || "left", originY: item.originY || "top", selectable: false, evented: false,
          });
          img.scaleToWidth(item.width);
          img.scaleToHeight(item.height);
          canvas.add(img);
        }

        if (item.type === "signature") {
          const img = await FabricImage.fromURL(item.src);
          img.set({
            left, top, originX: item.originX || "left", originY: item.originY || "top", angle: item.angle || 0, scaleX: item.scaleX || 1, scaleY: item.scaleY || 1, selectable: false, evented: false,
          });
          img.scaleToWidth(item.width);
          img.scaleToHeight(item.height);
          canvas.add(img);
        }

        if (item.type === "shape") {
          let shape;
          switch (item.shapeType) {
            case "ellipse":
              shape = new Ellipse({ rx: item.rx, ry: item.ry, fill: item.fill, stroke: item.stroke, strokeWidth: item.strokeWidth, strokeDashArray: item.dash });
              break;
            case "circle":
              shape = new Circle({ radius: item.radius, fill: item.fill, stroke: item.stroke, strokeWidth: item.strokeWidth, strokeDashArray: item.dash });
              break;
            case "rect":
              shape = new Rect({ width: item.width, height: item.height, fill: item.fill, stroke: item.stroke, strokeWidth: item.strokeWidth, strokeDashArray: item.dash, rx: item.rx || 0, ry: item.ry || 0 });
              break;
            case "line":
              shape = new Line([item.x1, item.y1, item.x2, item.y2], { stroke: item.stroke, strokeWidth: item.strokeWidth, strokeDashArray: item.dash });
              break;
            case "triangle":
              shape = new Triangle({ width: item.width, height: item.height, fill: item.fill, stroke: item.stroke, strokeWidth: item.strokeWidth, strokeDashArray: item.dash });
              break;
          }
          if (shape) {
            shape.set({ left, top, originX: item.originX || "center", originY: item.originY || "center", angle: item.angle || 0, scaleX: item.scaleX || 1, scaleY: item.scaleY || 1, selectable: false, evented: false });
            canvas.add(shape);
          }
        }
      }
      canvas.renderAll();
    };

    render();

    return () => {
      canvas.dispose();
    };
  }, [template, values, onCanvasReady]);

  return (
    <div 
      ref={canvasContainerRef} 
      style={{ 
        transform: `scale(${scale})`, 
        transformOrigin: "top left",
        width: `${(template?.canvasSize?.width || 900) / 16}rem`,
        height: `${(template?.canvasSize?.height || 600) / 16}rem`
      }}
    >
      <canvas ref={canvasRef} />
    </div>
  );
};

const ScaledFabricPreview = ({ parsedTemplate, values, onClick }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  const width = parsedTemplate?.canvasSize?.width || 900;
  const height = parsedTemplate?.canvasSize?.height || 600;

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setScale(entry.contentRect.width / width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [width]);

  return (
    <div 
      ref={containerRef} 
      className={`scaled-fabric-preview-container ${onClick ? "scaled-fabric-preview-clickable" : ""}`}
      style={{ aspectRatio: `${width} / ${height}` }}
      onClick={onClick}
    >
      <FabricCertificateRender template={parsedTemplate} values={values} scale={scale} />
    </div>
  );
};

const PreviewVariationsModal = ({ isOpen, toggle, parsedTemplate, onSave, isSaving }) => {
  const [fullScreenVariant, setFullScreenVariant] = useState(null);

  const toggleFullScreen = () => setFullScreenVariant(null);

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable className="modal-dialog-centered">
        <ModalHeader toggle={toggle}>
          Template Preview Variations
        </ModalHeader>
        <ModalBody className="bg-secondary">
          <Row>
            <Col md="4">
              <Card className="p-3 shadow-sm mb-4">
                <h5 className="text-center text-muted mb-3">Small Data</h5>
                <div className="preview-variant-card">
                  <ScaledFabricPreview 
                    parsedTemplate={parsedTemplate} 
                    values={certificateValuesSmall} 
                    onClick={() => setFullScreenVariant({ name: "Small Data", values: certificateValuesSmall })}
                  />
                </div>
              </Card>
            </Col>
            <Col md="4">
              <Card className="p-3 shadow-sm mb-4">
                <h5 className="text-center text-muted mb-3">Medium Data</h5>
                <div className="preview-variant-card">
                  <ScaledFabricPreview 
                    parsedTemplate={parsedTemplate} 
                    values={certificateValuesMedium} 
                    onClick={() => setFullScreenVariant({ name: "Medium Data", values: certificateValuesMedium })}
                  />
                </div>
              </Card>
            </Col>
            <Col md="4">
              <Card className="p-3 shadow-sm mb-4">
                <h5 className="text-center text-muted mb-3">Large Data</h5>
                <div className="preview-variant-card">
                  <ScaledFabricPreview 
                    parsedTemplate={parsedTemplate} 
                    values={certificateValuesLarge} 
                    onClick={() => setFullScreenVariant({ name: "Large Data", values: certificateValuesLarge })}
                  />
                </div>
              </Card>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={isSaving}>
            Cancel
          </Button>
          <Button color="deep-blue" onClick={onSave} disabled={isSaving}>
            {isSaving ? <i className="fa fa-spinner fa-spin mr-2" /> : null}
            Confirm & Save
          </Button>
        </ModalFooter>
      </Modal>

      {/* Full Screen View Modal */}
      <Modal isOpen={!!fullScreenVariant} toggle={toggleFullScreen} size="xl" className="modal-dialog-centered modal-xl-fullscreen">
        <ModalHeader toggle={toggleFullScreen} className="bg-light">
          {fullScreenVariant?.name} Preview
        </ModalHeader>
        <ModalBody className="d-flex justify-content-center align-items-center bg-secondary modal-body-scrollable-x">
          {fullScreenVariant && (
            <div 
              className="full-screen-preview-card"
              style={{
                width: `${(parsedTemplate?.canvasSize?.width || 900) / 16}rem`,
                height: `${(parsedTemplate?.canvasSize?.height || 600) / 16}rem`
              }}
            >
              <FabricCertificateRender 
                template={parsedTemplate} 
                values={fullScreenVariant.values} 
                scale={1} 
              />
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default PreviewVariationsModal;
